import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  openO2ODispute,
  getDisputeEligibility,
  getO2ODispute,
} from '../api/o2o';

/**
 * Phase 38A.5 · DisputeFormModal
 *
 * Buyer-side dispute submission form for a specific O2O session.
 * Shows the 8 canonical reasons and enforces a 10-character minimum
 * description. Displays the eligibility hint (closing deadline) fetched
 * from the eligibility endpoint so users understand the 48h window.
 */

const REASON_KEYS = [
  'no_show_creator',
  'poor_quality',
  'not_as_described',
  'harassment',
  'technical_issues',
  'duration_short',
  'community_guidelines_violation',
  'other',
];

const DisputeFormModal = ({ open, onClose, o2oSessionId }) => {
  const { t, i18n } = useTranslation('dispute');
  const [eligibility, setEligibility] = useState(null);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [alreadyOpen, setAlreadyOpen] = useState(null);
  const [success, setSuccess] = useState(false);

  const fmtDeadline = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    const locale = i18n.language === 'en' ? 'en-GB' : 'it-IT';
    return d.toLocaleString(locale, {
      day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
    });
  };

  useEffect(() => {
    if (!open || !o2oSessionId) return;
    setSubmitError(null);
    setSuccess(false);
    setReason('');
    setDescription('');
    (async () => {
      try {
        const el = await getDisputeEligibility(o2oSessionId);
        setEligibility(el);
        if (!el.allowed && el.reason === 'dispute_already_open') {
          const existing = await getO2ODispute(o2oSessionId);
          setAlreadyOpen(existing);
        }
      } catch (e) {
        setSubmitError(e?.response?.data?.detail || e?.message);
      }
    })();
  }, [open, o2oSessionId]);

  const handleSubmit = async () => {
    if (submitting) return;
    if (!reason) {
      setSubmitError(t('modal.reason_placeholder'));
      return;
    }
    if (description.trim().length < 10) {
      setSubmitError(t('modal.description_label'));
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await openO2ODispute(o2oSessionId, {
        reason,
        description: description.trim(),
      });
      setSuccess(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'unknown_error';
      setSubmitError(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dispute-form-modal">
        <DialogHeader>
          <DialogTitle>{t('modal.title')}</DialogTitle>
          <DialogDescription>
            {t('modal.subtitle')}
          </DialogDescription>
        </DialogHeader>

        {success && (
          <div
            className="rounded-md border border-emerald-500/20 bg-emerald-500/5 p-4 text-sm text-emerald-200"
            data-testid="dispute-form-success"
          >
            <CheckCircle2 className="mr-1 inline h-4 w-4" />
            {t('modal.success')}
          </div>
        )}

        {!success && eligibility && !eligibility.allowed && (
          <div
            className="rounded-md border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-200"
            data-testid="dispute-form-ineligible"
          >
            <AlertTriangle className="mr-1 inline h-4 w-4" />
            {eligibility.reason && t(`ineligible.${eligibility.reason}`, { defaultValue: eligibility.reason })}
            {alreadyOpen && alreadyOpen.exists && (
              <p className="mt-1 text-xs text-red-200/80">
                {alreadyOpen.state}
              </p>
            )}
          </div>
        )}

        {!success && eligibility && eligibility.allowed && (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('modal.reason_label')}
              </label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="dispute-form-reason-trigger">
                  <SelectValue placeholder={t('modal.reason_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {(eligibility.reasons || REASON_KEYS).map((r) => (
                    <SelectItem key={r} value={r} data-testid={`dispute-reason-${r}`}>
                      {t(`reasons.${r}`, { defaultValue: r })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('modal.description_label')}
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                maxLength={4000}
                placeholder={t('modal.description_placeholder')}
                data-testid="dispute-form-description"
              />
              <p className="mt-1 text-right text-[10px] text-muted-foreground">
                {description.length} / 4000
              </p>
            </div>
            {eligibility.closes_at && (
              <p className="text-[11px] text-muted-foreground">
                {t('modal.deadline_hint', { time: fmtDeadline(eligibility.closes_at) })}
              </p>
            )}
            {submitError && (
              <div
                className="rounded-md border border-red-500/20 bg-red-500/5 p-3 text-xs text-red-200"
                data-testid="dispute-form-error"
              >
                {submitError}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            data-testid="dispute-form-cancel"
          >
            {success ? t('modal.close') : t('modal.cancel')}
          </Button>
          {!success && eligibility?.allowed && (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="gold-gradient"
              data-testid="dispute-form-submit"
            >
              {submitting ? (
                <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> {t('modal.submitting')}</>
              ) : t('modal.submit')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DisputeFormModal;
